//? React Query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

//* Lists API
import { questionListsAPI } from "./questionListsAPI";

const QUESTION_LISTS_KEY = ["question-lists"] as const;

/**
 * Data hook for the named question lists (favorites). Mirrors the cart hook
 * pattern: React Query wrapping the API object, token passed explicitly.
 */
export const useQuestionLists = (token: string) => {
  const queryClient = useQueryClient();

  const {
    data: lists = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: QUESTION_LISTS_KEY,
    queryFn: () => questionListsAPI.getAll(token),
    enabled: !!token,
    staleTime: 60 * 1000,
    retry: false,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: QUESTION_LISTS_KEY });
  };

  const createMutation = useMutation({
    mutationFn: (name: string) => questionListsAPI.create(name, token),
    onSuccess: invalidate,
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      questionListsAPI.rename(id, name, token),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => questionListsAPI.remove(id, token),
    onSuccess: invalidate,
  });

  const addItemMutation = useMutation({
    mutationFn: ({ listId, questionId }: { listId: string; questionId: string }) =>
      questionListsAPI.addItem(listId, questionId, token),
    onSuccess: invalidate,
  });

  const removeItemMutation = useMutation({
    mutationFn: ({ listId, questionId }: { listId: string; questionId: string }) =>
      questionListsAPI.removeItem(listId, questionId, token),
    onSuccess: invalidate,
  });

  return {
    lists,
    loading: isLoading || isFetching,
    isError,
    error: isError ? error : null,
    refetch,
    createList: createMutation.mutateAsync,
    renameList: renameMutation.mutateAsync,
    deleteList: deleteMutation.mutateAsync,
    addItem: addItemMutation.mutateAsync,
    removeItem: removeItemMutation.mutateAsync,
    isMutating:
      createMutation.isPending ||
      renameMutation.isPending ||
      deleteMutation.isPending ||
      addItemMutation.isPending ||
      removeItemMutation.isPending,
  };
};
